import { ArrHlp } from 'src/lib/fliko/helpers/array.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { EstateQrCodeData } from 'src/lib/fliko/models/estate/estate-qr-code.data';
import { Nullish } from 'src/lib/fliko/types/typeish.type';

export type EstateQrContext = {
  id?: string | null;
  name?: string | null;
  type?: EstateType | null;
};

export type EstateQrRouteData = {
  type: 'estate_qr';
  estateId: string | null;
  estateName: string | null;
  estateType: EstateType | null;
  qrCodeId: string | null;
  qrCodeName: string | null;
  locationType: string | null;
  buildingNumber: string | null;
  stairwayNumber: string | null;
  floor: string | null;
  number: string | null;
};
export type ResolvedEstateQrRouteData = EstateQrRouteData & {
  estateId: string;
  qrCodeId: string;
};

type EstateQrRouteSource = Record<string, unknown> | Nullish;

function parseEstateType(value: string | Nullish): EstateType | null {
  return value && Object.values(EstateType).includes(value as EstateType) ? (value as EstateType) : null;
}

export function createEstateQrRouteData(estate: EstateQrContext | Nullish, data?: EstateQrCodeData | null): EstateQrRouteData {
  return {
    type: 'estate_qr',
    estateId: StrHlp.trimToNull(estate?.id),
    estateName: StrHlp.trimToNull(estate?.name),
    estateType: estate?.type ?? null,
    qrCodeId: StrHlp.trimToNull(data?.id),
    qrCodeName: StrHlp.trimToNull(data?.name),
    locationType: StrHlp.trimToNull(data?.locationType),
    buildingNumber: StrHlp.trimToNull(data?.buildingNumber),
    stairwayNumber: StrHlp.trimToNull(data?.stairwayNumber),
    floor: StrHlp.trimToNull(data?.floor),
    number: StrHlp.trimToNull(data?.number)
  };
}

export function buildEstateQrPublicUrl(data?: EstateQrCodeData | null): string | null {
  return StrHlp.trimToNull(data?.dynamicLink);
}

export function buildEstateQrShortPath(qrCodeId: string | Nullish): string {
  const trimmedQrCodeId = StrHlp.trimToNull(qrCodeId);
  if (!trimmedQrCodeId) return '/qr/estate';

  const params = new URLSearchParams();
  params.set('qrCodeId', trimmedQrCodeId);
  return `/qr/estate?${params.toString()}`;
}

export function buildEstateQrShortUrl(origin: string, qrCodeId: string | Nullish): string {
  return new URL(buildEstateQrShortPath(qrCodeId), origin).toString();
}

export function hasEstateQrRoutePayload(qrData: EstateQrRouteData | Nullish): qrData is ResolvedEstateQrRouteData {
  return !!(qrData?.estateId && qrData?.qrCodeId);
}

export function normalizeEstateQrRouteData(source: EstateQrRouteSource): EstateQrRouteData | null {
  if (!source) return null;

  const getParam = (key: string): string | null => {
    const rawValue = source[key];
    const value = ArrHlp.get(rawValue, 0);
    if (typeof value === 'string') return StrHlp.trimToNull(value);
    if (typeof value === 'number' || typeof value === 'boolean') {
      return StrHlp.trimToNull(String(value));
    }
    return null;
  };

  const payload: EstateQrRouteData = {
    type: 'estate_qr',
    estateId: getParam('estateId'),
    estateName: getParam('estateName'),
    estateType: parseEstateType(getParam('estateType')),
    qrCodeId: getParam('qrCodeId'),
    qrCodeName: getParam('qrCodeName'),
    locationType: getParam('locationType'),
    buildingNumber: getParam('buildingNumber'),
    stairwayNumber: getParam('stairwayNumber'),
    floor: getParam('floor'),
    number: getParam('number')
  };

  const hasContent = Object.entries(payload).some(([key, value]) => key !== 'type' && value != null);
  return hasContent ? payload : null;
}

export function parseEstateQrRouteData(queryParams: Record<string, unknown>): EstateQrRouteData | null {
  const getRouteParam = (key: string): string | null => {
    const value = ArrHlp.get(queryParams[key], 0);
    return typeof value === 'string' ? StrHlp.trimToNull(value) : null;
  };

  const qrCodeId = getRouteParam('qrCodeId');

  if (qrCodeId) {
    return {
      type: 'estate_qr',
      estateId: null,
      estateName: null,
      estateType: null,
      qrCodeId,
      qrCodeName: null,
      locationType: null,
      buildingNumber: null,
      stairwayNumber: null,
      floor: null,
      number: null
    };
  }

  const estateId = getRouteParam('estateId');

  return estateId
    ? {
        type: 'estate_qr',
        estateId,
        estateName: null,
        estateType: null,
        qrCodeId: null,
        qrCodeName: null,
        locationType: null,
        buildingNumber: null,
        stairwayNumber: null,
        floor: null,
        number: null
      }
    : null;
}
